import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const BLANK_SEA = { vessel_name: "", vessel_type: "", grt_bhp: "", company: "", rank: "", date_from: "", date_to: "" };

export const rowDays = (r) => {
  if (!r.date_from || !r.date_to) return 0;
  return Math.max(Math.round((new Date(r.date_to) - new Date(r.date_from)) / 864e5) + 1, 0);
};

export const fmtDays = (n) => {
  const y = Math.floor(n / 365), m = Math.min(Math.floor((n % 365) / 30), 11), d = (n % 365) - m * 30;
  return [y && `${y}y`, m && `${m}m`, (d || !(y || m)) && `${d}d`].filter(Boolean).join(" ");
};

export const rankTotals = (rows) => {
  const per = {};
  rows.forEach((r) => { if (r.rank) per[r.rank] = (per[r.rank] || 0) + rowDays(r); });
  return { per: Object.entries(per), total: rows.reduce((s, r) => s + rowDays(r), 0) };
};

const COLS = [["vessel_name", "Vessel", "text"], ["vessel_type", "Type", "text"], ["grt_bhp", "GRT/BHP", "text"], ["company", "Company", "text"],
  ["rank", "Rank held", "text"], ["date_from", "From", "date"], ["date_to", "To", "date"]];

export function SeaServiceEditor({ rows, onChange, ranks = [], vesselTypes = [], prefix = "sea" }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const { per, total } = rankTotals(rows);
  return (
    <div className="space-y-3">
      <datalist id={`${prefix}-ranks`}>{ranks.map((r) => <option key={r} value={r} />)}</datalist>
      <datalist id={`${prefix}-types`}>{vesselTypes.map((r) => <option key={r} value={r} />)}</datalist>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm min-w-[980px]" data-testid={`${prefix}-table`}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>{COLS.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}<th className="px-2 py-2 text-left font-medium">Duration</th><th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">No sea service added yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`${prefix}-row-${i}`}>
                {COLS.map(([k, l, t]) => (
                  <td key={k} className="px-1.5 py-1.5">
                    <Input type={t} value={r[k] || ""} aria-label={l} list={k === "rank" ? `${prefix}-ranks` : k === "vessel_type" ? `${prefix}-types` : undefined}
                      className={`h-8 text-sm ${t === "date" ? "w-[140px]" : ""}`} onChange={(e) => set(i, k, e.target.value)} data-testid={`${prefix}-${k}-${i}`} />
                  </td>
                ))}
                <td className="px-2 py-1.5 font-mono text-xs text-slate-600 whitespace-nowrap" data-testid={`${prefix}-duration-${i}`}>{rowDays(r) ? fmtDays(rowDays(r)) : "—"}</td>
                <td className="px-1"><Button type="button" size="icon" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove row" data-testid={`${prefix}-remove-${i}`}><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { ...BLANK_SEA }])} data-testid={`${prefix}-add-row-btn`}><Plus className="w-4 h-4 mr-1" /> Add sea service</Button>
        <div className="text-sm text-slate-600 space-y-0.5" data-testid={`${prefix}-totals`}>
          {per.map(([k, v]) => <div key={k}><span className="text-slate-400">{k}:</span> <span className="font-mono">{fmtDays(v)}</span></div>)}
          <div className="font-semibold text-slate-800">Total sea service: <span className="font-mono" data-testid={`${prefix}-total`}>{fmtDays(total)}</span></div>
        </div>
      </div>
    </div>
  );
}
